/**
 * Build & schedule the pipeline report, in one place with room to read it.
 *
 * What this replaces: a "Versions & schedules" tab that gave the whole job —
 * reading what a schedule already does, and filling in twelve fields to make
 * a new one — a single ~45%-width column, itself inside a tab strip, itself
 * inside an embedded panel capped at 25vh of screen. Twelve fields to a grid
 * of six narrow columns is not a form a desk reads before it fires; it is a
 * form a desk avoids until a report is actually late.
 *
 * Every schedule row here says what actually happened last time — sent,
 * delivered, opened, or failed with the reason — cross-referenced against the
 * same delivery ledger the Reporting page's own Delivery History tab reads,
 * not the single terse `lastRunStatus` string the old panel showed. A desk
 * asking "did this go out" gets an answer, not a status code.
 *
 * The report's own design — columns, filters, style, totals — stays in the
 * Studio's own panels. Rebuilding that editor a second time in here would give
 * the report two definitions with two chances to disagree; this shows what
 * those panels currently produce and links back to them instead.
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Calendar,
  CheckCircle2,
  Clock3,
  Loader2,
  Mail,
  MailWarning,
  Pause,
  Play,
  Trash2,
  Zap,
} from 'lucide-react'
import clsx from 'clsx'
import DialogShell from '@/components/crm/DialogShell'
import { pipelineViewsService } from '@/services/pipelineViews'
import { reportSchedulesService, type ReportSchedulerStatus } from '@/services/reportSchedules'
import { SchedulerStatusNotice } from '@/pages/reporting/SchedulerStatusNotice'
import { nextRunText, useSchedulerStatus } from '@/pages/reporting/schedulerStatus'
import type {
  SavedView,
  ScheduleDeliveryMode,
  ScheduleFormat,
  ScheduleFrequency,
  ScheduleTemplate,
  ScheduleTriggerType,
  ViewSchedule,
  ViewScheduleInput,
} from './types'
import { SCHEDULE_TIMEZONES } from './types'
import {
  DAY_LABELS,
  FREQUENCIES,
  apiErrorMessage,
  compactField,
  goldBtn,
  ghostBtn,
  labelClass,
  optionClass,
  parseEmails,
} from './studioUi'
import { clockLabel, parseTimeInput, timeInputValue } from './scheduleClock'

function relativeOrDash(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.valueOf())) return '—'
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date)
}

/** What actually happened on a schedule's most recent run, read off the same
 *  delivery ledger the Delivery History tab shows — not the schedule row's
 *  own terse status string, which the ledger supersedes whenever it has an
 *  answer. Falls back to that string only for a schedule the ledger has
 *  never recorded an attempt for (a "retain only" run, or one from before
 *  delivery tracking existed). */
function lastOutcomeFor(
  schedule: ViewSchedule,
  historyItems: { scheduleId: string; status: string; recipient: string | null; sentAt: string | null; deliveredAt: string | null; openedAt: string | null; completedAt: string | null; error: string | null }[],
) {
  const forThis = historyItems
    .filter((item) => item.scheduleId === schedule.id)
    .sort((a, b) => (b.completedAt ?? b.sentAt ?? '').localeCompare(a.completedAt ?? a.sentAt ?? ''))
  const latest = forThis[0]
  if (latest) {
    return {
      status: latest.status,
      recipient: latest.recipient,
      when: latest.openedAt ?? latest.deliveredAt ?? latest.sentAt ?? latest.completedAt,
      error: latest.error,
    }
  }
  if (schedule.lastRunAt) {
    return {
      status: schedule.lastRunStatus ?? 'unknown',
      recipient: null,
      when: schedule.lastRunAt,
      error: null,
    }
  }
  return null
}

function OutcomeBadge({ outcome }: { outcome: ReturnType<typeof lastOutcomeFor> }) {
  if (!outcome) {
    return <span className="inline-flex items-center gap-1 text-[11px] text-stone-500"><Clock3 className="h-3 w-3" /> Not yet run</span>
  }
  const tone =
    outcome.status === 'failed'
      ? 'text-red-300'
      : outcome.status === 'opened' || outcome.status === 'delivered' || outcome.status === 'sent'
        ? 'text-emerald-300'
        : 'text-stone-400'
  const Icon = outcome.status === 'failed' ? MailWarning : outcome.status === 'retained' ? Clock3 : CheckCircle2
  const label =
    outcome.status === 'failed'
      ? `Failed${outcome.error ? `: ${outcome.error}` : ''}`
      : outcome.status === 'retained'
        ? 'Generated, retained only'
        : outcome.status === 'opened'
          ? `Opened${outcome.recipient ? ` by ${outcome.recipient}` : ''}`
          : outcome.status === 'delivered'
            ? `Delivered${outcome.recipient ? ` to ${outcome.recipient}` : ''}`
            : outcome.status === 'sent'
              ? `Sent${outcome.recipient ? ` to ${outcome.recipient}` : ''}`
              : outcome.status
  return (
    <div className={clsx('flex items-start gap-1 text-[11px]', tone)}>
      <Icon className="mt-0.5 h-3 w-3 shrink-0" />
      <span>
        {label}
        {outcome.when ? <span className="text-stone-600"> · {relativeOrDash(outcome.when)}</span> : null}
      </span>
    </div>
  )
}

/** Why a due run would not email, in the schedule's own words: every gate the
 *  server found closed. A schedule badge reading 'Active' is not an email that
 *  goes out, so this says which switch says otherwise. */
function DeliveryTruth({ schedule }: { schedule: ViewSchedule }) {
  if (schedule.willEmail) {
    return (
      <div
        data-testid="delivery-truth"
        className="mt-1 flex items-start gap-1 text-[10px] text-emerald-300"
      >
        <Mail className="mt-0.5 h-3 w-3 shrink-0" />
        <span>Each run is emailed to {schedule.recipients.join(', ')}.</span>
      </div>
    )
  }
  const blockers = schedule.deliveryBlockers ?? []
  if (blockers.length === 0) return null
  return (
    <div
      role="note"
      data-testid="delivery-truth"
      className="mt-1 rounded border border-amber-400/25 bg-amber-400/[0.05] px-2 py-1 text-[10px] text-amber-100/90"
    >
      <div className="flex items-center gap-1 font-medium">
        <MailWarning className="h-3 w-3 shrink-0" /> No email will be sent while:
      </div>
      <ul className="mt-0.5 list-disc pl-4">
        {blockers.map((blocker) => (
          <li key={blocker.code} title={blocker.setting ? `Governed by ${blocker.setting}` : undefined}>
            {blocker.label}
          </li>
        ))}
      </ul>
    </div>
  )
}

/** The same question for a schedule not yet made, from the deployment's switches.
 *  Only a switch the server reports closed is named; one it does not report is not guessed. */
function formDeliveryBlockers(
  status: ReportSchedulerStatus | undefined,
  mode: ScheduleDeliveryMode | undefined,
): string[] {
  if (!status || !mode || mode === 'none') return []
  const out: string[] = []
  if (status.generationEnabled === false) {
    out.push('Scheduled generation is switched off on this deployment, so no schedule runs on its own.')
  }
  if (status.autosendEnabled === false) {
    out.push('The master email switch is off on this deployment.')
  }
  if (mode === 'reviewer' && status.reviewerDeliveryEnabled === false) {
    out.push('Reviewer delivery is switched off on this deployment.')
  }
  if (mode === 'team' && status.teamDeliveryEnabled === false) {
    out.push('Team delivery is switched off on this deployment.')
  }
  if (status.allowlistConfigured === false) {
    out.push('No recipient is approved for scheduled email yet (the delivery allowlist is empty).')
  }
  return out
}

function defaultScheduleTemplate(viewId: string): ScheduleTemplate {
  return viewId === 'current-status' ? 'current_status' : 'pipeline_summary'
}

const DEFAULT_FORM = (viewName: string, viewId: string): ViewScheduleInput => ({
  name: `${viewName} schedule`,
  template: defaultScheduleTemplate(viewId),
  frequency: 'weekly',
  dayOfWeek: 0,
  dayOfMonth: 1,
  hourUtc: 14,
  minuteLocal: 0,
  timezone: 'America/New_York',
  businessDaysOnly: true,
  triggerType: 'time',
  triggerEventType: null,
  recipients: [],
  format: 'pdf',
  deliveryMode: 'none',
  isActive: false,
  pinVersion: null,
})

export function PipelineReportScheduleModal({
  view,
  rowCount,
  columnCount,
  columnNames,
  activeFilterCount,
  groupByLabel,
  unsaved = false,
  onInvalidated,
  onClose,
}: {
  view: SavedView
  /** What the SAVED version of the report holds — a schedule runs that, not a draft. */
  rowCount: number
  columnCount: number
  columnNames: string[]
  activeFilterCount: number
  groupByLabel: string | null
  /** The Studio's draft differs from the saved version. */
  unsaved?: boolean
  onInvalidated: () => Promise<void>
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [schedForm, setSchedForm] = useState<ViewScheduleInput>(() => DEFAULT_FORM(view.name, view.id))
  const [recipientsRaw, setRecipientsRaw] = useState('')
  const [scheduleError, setScheduleError] = useState<string | null>(null)

  // REG-05: what the scheduler on this deployment will actually do, and the
  // events the product actually emits for an event trigger.
  const schedulerStatus = useSchedulerStatus().data
  const productEvents = schedulerStatus?.productEvents ?? []

  const schedulesKey = ['pipeline-view-schedules', view.id] as const
  const schedulesQuery = useQuery({
    queryKey: schedulesKey,
    queryFn: () => pipelineViewsService.listSchedules(view.id),
  })

  // Same endpoint the Delivery History tab reads — one ledger, read from two
  // places, so a schedule here never claims a status that page would dispute.
  const historyQuery = useQuery({
    queryKey: ['reports', 'delivery-history', 150],
    queryFn: () => reportSchedulesService.getDeliveryHistory(150),
    staleTime: 30_000,
  })
  const historyItems = historyQuery.data?.items ?? []

  const invalidateSchedules = async () => {
    await queryClient.invalidateQueries({ queryKey: schedulesKey })
    await onInvalidated()
  }

  const createScheduleMutation = useMutation({
    mutationFn: (body: ViewScheduleInput) => pipelineViewsService.createSchedule(view.id, body),
    onSuccess: async () => {
      setScheduleError(null)
      setRecipientsRaw('')
      setSchedForm(DEFAULT_FORM(view.name, view.id))
      await invalidateSchedules()
    },
    onError: (err: unknown) => setScheduleError(apiErrorMessage(err, 'Failed to create schedule')),
  })

  const updateScheduleMutation = useMutation({
    mutationFn: ({ scheduleId, body }: { scheduleId: string; body: Partial<ViewScheduleInput> }) =>
      pipelineViewsService.updateSchedule(view.id, scheduleId, body),
    onSuccess: async () => {
      setScheduleError(null)
      await invalidateSchedules()
    },
    onError: (err: unknown) => setScheduleError(apiErrorMessage(err, 'Failed to update schedule')),
  })

  const deleteScheduleMutation = useMutation({
    mutationFn: (scheduleId: string) => pipelineViewsService.deleteSchedule(view.id, scheduleId),
    onSuccess: async () => {
      setScheduleError(null)
      await invalidateSchedules()
    },
    onError: (err: unknown) => setScheduleError(apiErrorMessage(err, 'Failed to delete schedule')),
  })

  const [previewNote, setPreviewNote] = useState<{ scheduleId: string; filename: string | null } | null>(null)

  const runNowMutation = useMutation({
    mutationFn: (scheduleId: string) => pipelineViewsService.runScheduleNow(view.id, scheduleId),
    onSuccess: async (result, scheduleId) => {
      setScheduleError(null)
      setPreviewNote({ scheduleId, filename: typeof result.filename === 'string' ? result.filename : null })
      await invalidateSchedules()
      await queryClient.invalidateQueries({ queryKey: ['reports', 'delivery-history'] })
    },
    onError: (err: unknown) => setScheduleError(apiErrorMessage(err, 'Failed to run schedule')),
  })

  const activateScheduleMutation = useMutation({
    mutationFn: (scheduleId: string) => pipelineViewsService.activateSchedule(view.id, scheduleId),
    onSuccess: async () => {
      setScheduleError(null)
      await invalidateSchedules()
    },
    onError: (err: unknown) => setScheduleError(apiErrorMessage(err, 'Approval is required to activate this schedule')),
  })

  const busy =
    createScheduleMutation.isPending ||
    updateScheduleMutation.isPending ||
    deleteScheduleMutation.isPending ||
    runNowMutation.isPending ||
    activateScheduleMutation.isPending

  const submitSchedule = () => {
    const recipients = parseEmails(recipientsRaw)
    if (!schedForm.name.trim()) {
      setScheduleError('Schedule name is required')
      return
    }
    if (schedForm.deliveryMode === 'reviewer' && recipients.length !== 1) {
      setScheduleError('Reviewer delivery requires exactly one recipient email')
      return
    }
    if (schedForm.deliveryMode === 'team' && recipients.length === 0) {
      setScheduleError('Team delivery requires at least one recipient email')
      return
    }
    const body: ViewScheduleInput = {
      ...schedForm,
      name: schedForm.name.trim(),
      recipients: schedForm.deliveryMode === 'none' ? [] : recipients,
      dayOfWeek:
        schedForm.frequency === 'weekly' || schedForm.frequency === 'biweekly'
          ? Number(schedForm.dayOfWeek ?? 0)
          : null,
      dayOfMonth:
        schedForm.frequency === 'monthly' || schedForm.frequency === 'quarterly'
          ? Number(schedForm.dayOfMonth ?? 1)
          : null,
      pinVersion: schedForm.pinVersion == null ? null : Number(schedForm.pinVersion),
      isActive: false,
    }
    createScheduleMutation.mutate(body)
  }

  const formBlockers = formDeliveryBlockers(schedulerStatus, schedForm.deliveryMode)
  const schedules = schedulesQuery.data ?? []
  const activeCount = schedules.filter((s) => s.isActive).length
  const columnPreview = useMemo(() => {
    if (columnNames.length <= 4) return columnNames.join(', ')
    return `${columnNames.slice(0, 4).join(', ')}, +${columnNames.length - 4} more`
  }, [columnNames])

  return (
    <DialogShell label={`Schedule “${view.name}”`} onClose={onClose} width="landscape" testId="pipeline-schedule-modal">
      <div className="flex min-h-0 flex-1 flex-col">
        {/* What will actually be generated. Read-only — the Studio's own
            Columns, Filters, Totals and Style panels are the one place that changes
            it, so this never becomes a second, driftable definition. */}
        <div className="flex shrink-0 flex-wrap items-center gap-x-5 gap-y-1 border-b border-amber-400/10 bg-amber-400/[0.03] px-4 py-2.5 text-[11px] text-stone-400">
          <span className="text-[10px] uppercase tracking-[0.14em] text-amber-200/70">
            Saved v{view.version} includes
          </span>
          <span>{rowCount} row{rowCount === 1 ? '' : 's'}</span>
          <span>{columnCount} column{columnCount === 1 ? '' : 's'} — {columnPreview || 'none selected'}</span>
          <span>{activeFilterCount} report filter{activeFilterCount === 1 ? '' : 's'}</span>
          {groupByLabel ? <span>Grouped by {groupByLabel}</span> : null}
          <span className="text-stone-600">Adjust in the Columns, Filters, Totals and Style panels.</span>
        </div>
        {unsaved ? (
          <div
            role="alert"
            data-testid="schedule-unsaved-notice"
            className="mx-4 mt-2 flex shrink-0 items-start gap-2 rounded border border-amber-400/30 bg-amber-400/[0.08] px-2.5 py-1.5 text-[11px] text-amber-100"
          >
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-300" aria-hidden />
            <span>
              This report has unsaved changes. A schedule runs the saved version (v{view.version}), not the draft
              in the Studio — save a new version first. Activating a schedule is disabled until you do.
            </span>
          </div>
        ) : null}
        <div className="shrink-0 px-4 pt-2 empty:hidden">
          <SchedulerStatusNotice compact />
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-0 overflow-hidden lg:grid-cols-[1fr_1.1fr]">
          {/* Existing schedules, with what actually happened last time. */}
          <div className="flex min-h-0 flex-col overflow-y-auto border-b border-amber-400/10 p-4 lg:border-b-0 lg:border-r">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-[12px] font-medium text-stone-200">
                Schedules for this report {schedules.length > 0 ? `(${activeCount} active of ${schedules.length})` : ''}
              </h3>
              <a
                href="/reports?tab=delivery-history"
                className="text-[10px] text-amber-300/80 underline-offset-2 hover:underline"
              >
                View full delivery history
              </a>
            </div>

            {schedulesQuery.isLoading ? (
              <div className="flex items-center gap-2 text-[12px] text-stone-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading schedules…
              </div>
            ) : schedulesQuery.isError ? (
              <div className="rounded border border-red-400/20 bg-red-400/[0.06] p-3 text-[11px] text-red-300">
                Could not load schedules. Backend schedule endpoints may not be available.
              </div>
            ) : schedules.length === 0 ? (
              <div className="rounded border border-dashed border-stone-700 p-4 text-center text-[12px] text-stone-500">
                No schedules yet. Set one up on the right — new schedules start paused so a person
                confirms it before anything goes out.
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {schedules.map((schedule) => {
                  const outcome = lastOutcomeFor(schedule, historyItems)
                  return (
                    <div key={schedule.id} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 text-[13px] text-stone-100">
                            {schedule.name}
                            <span
                              className={clsx(
                                'rounded-full px-1.5 py-0.5 text-[9px] uppercase tracking-wide',
                                schedule.isActive
                                  ? 'bg-emerald-400/10 text-emerald-300'
                                  : 'bg-stone-700/50 text-stone-400',
                              )}
                            >
                              {schedule.isActive ? 'Active' : 'Paused'}
                            </span>
                          </div>
                          <div className="mt-0.5 text-[11px] text-stone-500">
                            {schedule.template === 'pipeline_daily'
                              ? 'Daily change-bar'
                              : schedule.template === 'current_status'
                                ? 'Current Status'
                                : 'Pipeline summary'} ·{' '}
                            {schedule.triggerType === 'event'
                              ? `on ${schedule.triggerEventType || 'pipeline event'}`
                              : `${schedule.frequency}, ${clockLabel(schedule.hourUtc, schedule.minuteLocal)} ${schedule.timezone}`}
                            {schedule.pinVersion != null ? ` · v${schedule.pinVersion}` : ' · latest version'}
                          </div>
                          <div className="mt-0.5 flex items-center gap-1 text-[11px] text-stone-500">
                            <Mail className="h-3 w-3" />
                            {schedule.format.toUpperCase()} · {schedule.deliveryMode === 'none' ? 'retained only' : schedule.recipients.join(', ') || 'no recipients set'}
                          </div>
                          <DeliveryTruth schedule={schedule} />
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          <button
                            type="button"
                            disabled={busy || (unsaved && !schedule.isActive)}
                            onClick={() =>
                              schedule.isActive
                                ? updateScheduleMutation.mutate({ scheduleId: schedule.id, body: { isActive: false } })
                                : activateScheduleMutation.mutate(schedule.id)
                            }
                            className={ghostBtn}
                            title={
                              schedule.isActive
                                ? 'Pause this schedule'
                                : unsaved
                                  ? 'Save the report first — a schedule runs the saved version'
                                  : 'Activate this schedule'
                            }
                          >
                            {schedule.isActive ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => runNowMutation.mutate(schedule.id)}
                            className={ghostBtn}
                            title="Generate a preview now, retained but not sent — does not count as a run or change the schedule's own record"
                          >
                            <Zap className="h-3 w-3" />
                          </button>
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => {
                              if (window.confirm(`Delete schedule “${schedule.name}”?`)) {
                                deleteScheduleMutation.mutate(schedule.id)
                              }
                            }}
                            className={clsx(ghostBtn, 'border-red-400/30 text-red-200 hover:bg-red-400/10')}
                            title="Delete this schedule"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                      <div className="mt-2 border-t border-white/[0.04] pt-2">
                        {historyQuery.isLoading ? (
                          <span className="text-[11px] text-stone-600">Checking delivery record…</span>
                        ) : (
                          <OutcomeBadge outcome={outcome} />
                        )}
                        {schedule.nextRunAt || (schedulerStatus && !schedulerStatus.generationEnabled) ? (
                          <div className="mt-0.5 flex items-center gap-1 text-[10px] text-stone-600">
                            <Calendar className="h-2.5 w-2.5" /> Next run{' '}
                            {nextRunText(schedulerStatus, schedule.nextRunAt, relativeOrDash)}
                          </div>
                        ) : null}
                        {previewNote?.scheduleId === schedule.id ? (
                          <div className="mt-1 text-[10px] text-sky-300">
                            Preview generated{previewNote.filename ? ` (${previewNote.filename})` : ''} — retained,
                            not sent, and not counted as a run.
                          </div>
                        ) : null}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* New schedule. */}
          <div className="flex min-h-0 flex-col gap-4 overflow-y-auto p-4">
            <h3 className="text-[12px] font-medium text-stone-200">New schedule</h3>

            <label className="block">
              <span className={labelClass}>Schedule name</span>
              <input
                value={schedForm.name}
                onChange={(e) => setSchedForm((current) => ({ ...current, name: e.target.value }))}
                className={compactField}
              />
            </label>

            <div>
              <div className="mb-2 text-[10px] uppercase tracking-[0.14em] text-stone-600">Timing</div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className={labelClass}>Template</span>
                  <select
                    aria-label="Template"
                    value={schedForm.template ?? 'pipeline_summary'}
                    onChange={(e) =>
                      setSchedForm((current) => ({
                        ...current,
                        template: e.target.value as ScheduleTemplate,
                        format: e.target.value === 'pipeline_daily' ? 'pdf' : current.format,
                      }))
                    }
                    className={compactField}
                  >
                    <option className={optionClass} value="pipeline_summary">Pipeline summary</option>
                    <option className={optionClass} value="pipeline_daily">Daily change bar</option>
                    <option className={optionClass} value="current_status">Current Status</option>
                  </select>
                </label>
                <label className="block">
                  <span className={labelClass}>Trigger</span>
                  <select
                    value={schedForm.triggerType ?? 'time'}
                    onChange={(e) =>
                      setSchedForm((current) => ({ ...current, triggerType: e.target.value as ScheduleTriggerType }))
                    }
                    className={compactField}
                  >
                    <option className={optionClass} value="time">Scheduled time</option>
                    <option className={optionClass} value="event">Pipeline event</option>
                  </select>
                </label>

                {schedForm.triggerType === 'event' ? (
                  <label className="col-span-2 block">
                    <span className={labelClass}>Event type</span>
                    {/* Only events the product records. The free-text box
                        suggested 'pipeline.stage_changed', which nothing emits,
                        so a schedule on it could never fire. */}
                    <select
                      aria-label="Event type"
                      value={schedForm.triggerEventType ?? ''}
                      onChange={(e) => setSchedForm((current) => ({ ...current, triggerEventType: e.target.value }))}
                      className={compactField}
                    >
                      <option className={optionClass} value="">
                        {productEvents.length ? 'Choose an event' : 'Loading events…'}
                      </option>
                      {productEvents.map((event) => (
                        <option key={event.type} className={optionClass} value={event.type}>
                          {event.label}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : (
                  <>
                    <label className="block">
                      <span className={labelClass}>Frequency</span>
                      <select
                        value={schedForm.frequency}
                        onChange={(e) =>
                          setSchedForm((current) => ({ ...current, frequency: e.target.value as ScheduleFrequency }))
                        }
                        className={compactField}
                      >
                        {FREQUENCIES.map((frequency) => (
                          <option className={optionClass} key={frequency} value={frequency}>{frequency}</option>
                        ))}
                      </select>
                    </label>
                    <label className="block">
                      <span className={labelClass}>Time of day (local to the timezone)</span>
                      {/* The hour and minute are read on the clock of the zone
                          chosen beside it, so 09:05 in New York fires at 9:05
                          New York time — not at 09:05 UTC. */}
                      <input
                        type="time"
                        value={timeInputValue(schedForm.hourUtc, schedForm.minuteLocal)}
                        onChange={(e) => {
                          const time = parseTimeInput(e.target.value)
                          if (time) {
                            setSchedForm((current) => ({ ...current, hourUtc: time.hour, minuteLocal: time.minute }))
                          }
                        }}
                        className={compactField}
                      />
                    </label>
                    <label className="block">
                      <span className={labelClass}>Timezone</span>
                      <select
                        value={schedForm.timezone ?? 'UTC'}
                        onChange={(e) => setSchedForm((current) => ({ ...current, timezone: e.target.value }))}
                        className={compactField}
                      >
                        {SCHEDULE_TIMEZONES.map((timezoneName) => (
                          <option className={optionClass} key={timezoneName} value={timezoneName}>{timezoneName}</option>
                        ))}
                      </select>
                    </label>
                    {schedForm.frequency === 'weekly' || schedForm.frequency === 'biweekly' ? (
                      <label className="block">
                        <span className={labelClass}>Day of week</span>
                        <select
                          value={schedForm.dayOfWeek ?? 0}
                          onChange={(e) => setSchedForm((current) => ({ ...current, dayOfWeek: Number(e.target.value) }))}
                          className={compactField}
                        >
                          {DAY_LABELS.map((label, index) => (
                            <option className={optionClass} key={label} value={index}>{label}</option>
                          ))}
                        </select>
                      </label>
                    ) : null}
                    {schedForm.frequency === 'monthly' || schedForm.frequency === 'quarterly' ? (
                      <label className="block">
                        <span className={labelClass}>Day of month</span>
                        <input
                          type="number"
                          min={1}
                          max={28}
                          value={schedForm.dayOfMonth ?? 1}
                          onChange={(e) => setSchedForm((current) => ({ ...current, dayOfMonth: Number(e.target.value) }))}
                          className={compactField}
                        />
                      </label>
                    ) : null}
                    <label className="flex items-end gap-1.5 pb-1.5 text-[11px] text-stone-300">
                      <input
                        type="checkbox"
                        checked={Boolean(schedForm.businessDaysOnly)}
                        onChange={(e) => setSchedForm((current) => ({ ...current, businessDaysOnly: e.target.checked }))}
                        className="rounded border-stone-600 bg-black/40 text-amber-300 focus:ring-amber-300/40"
                      />
                      Business days only
                    </label>
                  </>
                )}
              </div>
            </div>

            <div>
              <div className="mb-2 text-[10px] uppercase tracking-[0.14em] text-stone-600">Format &amp; version</div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className={labelClass}>Format</span>
                  <select
                    value={schedForm.format}
                    onChange={(e) => setSchedForm((current) => ({ ...current, format: e.target.value as ScheduleFormat }))}
                    className={compactField}
                  >
                    <option className={optionClass} value="pdf">PDF</option>
                    <option className={optionClass} value="xlsx" disabled={schedForm.template === 'pipeline_daily'}>XLSX</option>
                  </select>
                </label>
                <label className="block">
                  <span className={labelClass}>Version</span>
                  <select
                    value={schedForm.pinVersion == null ? '' : String(schedForm.pinVersion)}
                    onChange={(e) =>
                      setSchedForm((current) => ({
                        ...current,
                        pinVersion: e.target.value === '' ? null : Number(e.target.value),
                      }))
                    }
                    className={compactField}
                  >
                    <option className={optionClass} value="">Always latest</option>
                    {view.versions.map((version) => (
                      <option className={optionClass} key={version.version} value={version.version}>v{version.version}</option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            <div>
              <div className="mb-2 text-[10px] uppercase tracking-[0.14em] text-stone-600">Delivery</div>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className={labelClass}>Delivery mode</span>
                  <select
                    value={schedForm.deliveryMode ?? 'none'}
                    onChange={(e) => setSchedForm((current) => ({ ...current, deliveryMode: e.target.value as ScheduleDeliveryMode }))}
                    className={compactField}
                  >
                    <option className={optionClass} value="none">Retain only — no email</option>
                    <option className={optionClass} value="reviewer">One reviewer</option>
                    <option className={optionClass} value="team">Approved team</option>
                  </select>
                </label>
                <label className="block">
                  <span className={labelClass}>Recipients</span>
                  <input
                    value={recipientsRaw}
                    onChange={(e) => setRecipientsRaw(e.target.value)}
                    placeholder={schedForm.deliveryMode === 'reviewer' ? 'reviewer@firm.com' : 'analyst@firm.com, pm@firm.com'}
                    disabled={schedForm.deliveryMode === 'none'}
                    className={compactField}
                  />
                </label>
              </div>
              {formBlockers.length > 0 ? (
                <div
                  role="note"
                  data-testid="delivery-gate-notice"
                  className="mt-2 rounded border border-amber-400/25 bg-amber-400/[0.05] px-2.5 py-1.5 text-[10px] text-amber-100/90"
                >
                  <div className="font-medium">With this deployment&apos;s settings, no email will go out:</div>
                  <ul className="mt-0.5 list-disc pl-4">
                    {formBlockers.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-white/[0.06] pt-3">
              <div className="flex items-start gap-1.5 text-[10px] leading-snug text-stone-600">
                <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                New schedules start paused. Activation needs reports.approve; email needs the
                production recipient allowlist.
              </div>
              <button type="button" disabled={busy} onClick={submitSchedule} className={clsx(goldBtn, 'shrink-0')}>
                {createScheduleMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                Create schedule
              </button>
            </div>
            {scheduleError ? <div className="text-[11px] text-red-300">{scheduleError}</div> : null}
          </div>
        </div>
      </div>
    </DialogShell>
  )
}

export default PipelineReportScheduleModal
